import type { LayerRecord } from './types';

export type RGB = [number, number, number];

// Sampled from the GTO logo gradient (red / orange / teal / green) plus
// two complementary tones so a portal with many layers stays distinguishable.
const PALETTE: RGB[] = [
  [33, 160, 162], // brand teal
  [232, 127, 59], // brand orange
  [38, 168, 100], // brand green
  [210, 65, 80], // brand red
  [78, 113, 200], // complementary blue
  [156, 96, 196], // complementary purple
];

const STORAGE_KEY = 'rcmc-layer-colors';

function loadOverrides(): Record<string, RGB> {
  if (typeof localStorage === 'undefined') return {};
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, RGB>) : {};
  } catch {
    return {};
  }
}

const overrides: Record<string, RGB> = loadOverrides();

function persistOverrides(): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(overrides));
  } catch {
    // ignore quota / privacy mode failures
  }
}

export function getLayerColor(id: string, records: LayerRecord[]): RGB {
  if (overrides[id]) return overrides[id];
  const idx = records.findIndex((r) => r.id === id);
  return PALETTE[Math.max(0, idx) % PALETTE.length];
}

export function setLayerColor(id: string, color: RGB): void {
  overrides[id] = color;
  persistOverrides();
}

export function clearLayerColor(id: string): void {
  delete overrides[id];
  persistOverrides();
}

export function rgbToHex([r, g, b]: RGB): string {
  const h = (n: number) =>
    Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
  return `#${h(r)}${h(g)}${h(b)}`;
}

export function hexToRgb(hex: string): RGB | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
}
